"use client"

import * as React from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"

import { cn } from "../../lib/utils"
import { Button } from "./button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"

export interface BaseItem {
  value: string | number;
  label: string | number;
}

interface Props<T extends BaseItem> extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  placeholder?: string;
  input_placeholder?: string;
  nothing_found?: string;
  options: T[];
  onValueChange?: (value: T) => void; // unchanged
  selectedValue?: string;            // unchanged
  renderOption?: (item: T, selected: boolean) => React.ReactNode;
  renderHeader?: () => React.ReactNode;
  allowCustom?: boolean;
}

export const Combobox = <T extends BaseItem>({
  placeholder = 'Select...',
  input_placeholder = 'Search...',
  nothing_found = 'No items found',
  options = [],
  onValueChange,
  selectedValue,
  className,
  renderOption,
  renderHeader, 
  allowCustom = false,
  ...props
}: Props<T>) => {
  const [open, setOpen] = React.useState(false)
  const [value, setValue] = React.useState('')
  const [search, setSearch] = React.useState('')

  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    setValue(selectedValue ?? '')
  }, [selectedValue])

  const clearSelection = React.useCallback(() => {
    setValue('')
    setSearch('')
    // notify parent with an "empty" item; parent can treat value === '' as unset
    onValueChange?.({ value: '', label: '' } as T)
    setOpen(false)
  }, [onValueChange])

  return (
    <div ref={containerRef} className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            {...props}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn("w-full justify-between whitespace-break-spaces pr-9", className)}
          >
            {value
              ? (
                options.find((framework) => framework.value.toString().toLowerCase() === value.toString().toLowerCase())
                  ?.label ?? value
              )
              : placeholder}
            <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
            {value && (
              <span
                className="absolute right-2 inline-flex p-1 rounded hover:bg-muted cursor-pointer"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  clearSelection()
                }}
                aria-label="Clear selection"
                title="Clear selection"
              >
                <X className="w-4 h-4 opacity-70" />
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0 max-w-[100dvw]" container={containerRef.current}>
          <Command
            filter={(value, search) => {
              if (value.toLowerCase().includes(search?.toLowerCase())) return 1;
              return 0;
            }}
          >
            <CommandInput
              placeholder={input_placeholder}
              value={search}
              onValueChange={setSearch}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  const match = options.find(
                    (opt) => opt.label.toString().toLowerCase() === search.toLowerCase()
                  )
                  if (!match && allowCustom !== false && search.trim()) {
                    const custom = { label: search, value: search } as T
                    onValueChange?.(custom)
                    setOpen(false)
                    setSearch('')
                  }
                }
                // Backspace-to-clear when search is empty and something is selected
                if (e.key === 'Backspace' && search === '' && value) {
                  e.preventDefault()
                  clearSelection()
                }
              }}
            />
            <CommandEmpty>{nothing_found}</CommandEmpty>
            <CommandGroup>
              <CommandList>
                {renderHeader?.()}

                {/* Clear row (shown only when something is selected) */}
                {value && (
                  <CommandItem
                    value="__clear__"
                    onSelect={clearSelection}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Clear selection
                  </CommandItem>
                )}

                {options.map((framework, index) => {
                  const selected = value === framework.value.toString()

                  return (
                    <CommandItem
                      value={`${framework.label.toString()}`}
                      key={`${framework.label}-${index}`}
                      onSelect={() => {
                        // Toggle off if selecting the same item again
                        if (selected) {
                          clearSelection()
                          return
                        }
                        setValue(framework.value.toString())
                        onValueChange?.(framework)
                        setSearch('')
                        setOpen(false)
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {renderOption
                        ? renderOption(framework as T, selected)
                        : <p className="max-w-96 whitespace-break-spaces">{framework.label}</p>}
                    </CommandItem>
                  )
                })}

                {allowCustom !== false && search &&
                  !options.some(opt => opt.label.toString().toLowerCase() === search.toLowerCase()) && (
                    <CommandItem
                      value={search}
                      onSelect={() => {
                        const custom = { label: search, value: search } as T
                        onValueChange?.(custom)
                        setOpen(false)
                        setSearch('')
                      }}
                    >
                      <span className="mr-2">➕</span>
                      <span>Toevoegen als nieuw merk: “{search}”</span>
                    </CommandItem>
                  )}
              </CommandList>
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
